/**
 * The email address someone signs up or applies with is carried to the next
 * page in this tab's sessionStorage, not in the address.
 *
 * Why (2026-11-05): Google Analytics and PostHog record page addresses, query
 * string included. Until then every new account went on to
 * /verify-email?email=..., and every pharmacy that used "Apply to join" on
 * /for-pharmacies went on to /register?role=pharmacy&email=...&name=..., so
 * both tools were sent the person's email address (and the pharmacy's name).
 * Google Analytics does not allow personal information such as email
 * addresses, and analytics have no use for them.
 *
 * Old links and bookmarks can still carry ?email= or ?name=, so the pages that
 * read them take them out of the address as soon as they load, before the
 * analytics scripts start (those load once the page is idle), and PostHog
 * strips them from any URL it records (PostHogProvider.tsx).
 */

const SIGNUP_EMAIL_KEY = 'su_signup_email';

/** Query keys that carry personal details. */
export const PERSONAL_QUERY_KEYS = ['email', 'name'] as const;

/** Keeps the address a new account was created with, for "Check your inbox" and its resend button. */
export function rememberSignupEmail(email: string): void {
  try {
    window.sessionStorage.setItem(SIGNUP_EMAIL_KEY, email.trim());
  } catch {
    // storage unavailable: the inbox page asks for nothing it cannot show
  }
}

export function rememberedSignupEmail(): string {
  try {
    return window.sessionStorage.getItem(SIGNUP_EMAIL_KEY) || '';
  } catch {
    return '';
  }
}

/**
 * Reads ?email= and ?name= from the current address and removes them, keeping
 * every other parameter and the fragment. Returns the values that were there.
 */
export function takePersonalQueryParams(): { email: string; name: string } {
  if (typeof window === 'undefined') return { email: '', name: '' };
  try {
    const url = new URL(window.location.href);
    const email = (url.searchParams.get('email') || '').trim();
    const name = (url.searchParams.get('name') || '').trim();
    if (PERSONAL_QUERY_KEYS.some((key) => url.searchParams.has(key))) {
      for (const key of PERSONAL_QUERY_KEYS) url.searchParams.delete(key);
      window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`);
    }
    return { email, name };
  } catch {
    return { email: '', name: '' };
  }
}

/** A plausible email address, short enough to be one. */
export function looksLikeEmail(value: string): boolean {
  return value.length <= 200 && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
}
